import React, { useEffect, useCallback, useMemo } from 'react';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import { Switch } from 'app/components/ui/switch';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from 'app/components/ui/select';
import { Button } from 'app/components/ui/button';
import { PlusIcon, TrashIcon } from '@radix-ui/react-icons';
import { useFormContext } from 'react-hook-form';

function FormFields({ initialFields = [] }) {
  const form = useFormContext();

  // Sync initialFields to form state
  useEffect(() => {
    if (!form.getValues('fields')?.length) {
      form.setValue(
        'fields',
        initialFields.map((field: any) => ({
          field_id: field.field_id,
          field_name: field.field_name || '',
          field_type: field.field_type || 'text',
          is_required: Boolean(field.is_required),
        })),
      );
    }
  }, [initialFields, form]);

  // Watch fields dynamically
  const fields = form.watch('fields') || [];

  // Memoize handlers to prevent unnecessary re-renders
  const handleAddFields = useCallback(() => {
    const newField = {
      field_id: null, // New fields have no ID initially
      field_name: '',
      field_type: 'text',
      is_required: true,
    };
    const updatedFields = [...fields, newField];
    form.setValue('fields', updatedFields);
  }, [fields, form]);

  const handleFieldChange = useCallback(
    (index: number, key: string, value: any) => {
      const updatedFields = fields.map((field: any, i: number) =>
        i === index ? { ...field, [key]: value } : field,
      );
      form.setValue('fields', updatedFields);
    },
    [fields, form],
  );

  const handleDeleteField = useCallback(
    (index: number) => {
      const updatedFields = fields.filter((_: any, i: number) => i !== index);
      form.setValue('fields', updatedFields);
    },
    [fields, form],
  );

  // Memoize fields list to prevent unnecessary re-renders
  const fieldsList = useMemo(() => {
    return fields.map((field: any, index: number) => (
      <div className="border-b py-4" key={index}>
        <div className="flex items-center gap-4">
          {/* Field Name */}
          <div className="flex-1">
            <Label
              htmlFor={`field_name_${index}`}
              className="font-medium text-sm mb-4 block"
            >
              Field Name
            </Label>
            <Input
              id={`field_name_${index}`}
              placeholder="Enter field name"
              className="w-full"
              value={field.field_name}
              onChange={e =>
                handleFieldChange(index, 'field_name', e.target.value)
              }
            />
          </div>

          {/* Field Type */}
          <div className="flex-1">
            <Label
              htmlFor={`field_type_${index}`}
              className="font-medium text-sm mb-4 block"
            >
              Field Type
            </Label>
            <Select
              onValueChange={value =>
                handleFieldChange(index, 'field_type', value)
              }
              value={field.field_type}
            >
              <SelectTrigger id={`field_type_${index}`} className="w-full">
                <SelectValue placeholder="Select field type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="text">Text</SelectItem>
                <SelectItem value="select">Select</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Is Required */}
          <div className="flex items-center mt-[34px]">
            <Label
              htmlFor={`is_required_${index}`}
              className="font-medium text-sm mr-4 mb-0"
            >
              Is Required
            </Label>
            <Switch
              id={`is_required_${index}`}
              checked={field.is_required}
              onCheckedChange={value =>
                handleFieldChange(index, 'is_required', value)
              }
            />
          </div>

          {/* Delete Button */}
          <div className="mt-[34px]">
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleDeleteField(index)}
            >
              <TrashIcon className="w-5 h-5 text-red-500" />
            </Button>
          </div>
        </div>
      </div>
    ));
  }, [fields, handleFieldChange, handleDeleteField]);

  return (
    <div className="bg-white dark:bg-transparent rounded-lg px-4 py-5 mb-5">
      <Label>Order Processing Details:</Label>

      {fieldsList}

      <div className="p-2 text-right mr-3">
        <Button type="button" onClick={handleAddFields}>
          <PlusIcon className="w-5 h-5 mr-2" />
          Add Fields
        </Button>
      </div>
    </div>
  );
}

export default FormFields;
